/* eslint-disable jsx-a11y/control-has-associated-label */
import { Trans, useTranslation } from 'react-i18next';
import { isMobile } from 'react-device-detect';
import React, { useContext, useMemo } from 'react';
import { UpgradeAccountContext } from '../../../../../contexts/settings/upgradeAccount/context';
import { useSetting } from '../../../../../utilities/sharedHooks/settings';
import { Typography } from '../../../../../components';

const VerificationNotes = () => {
  const { t: translate } = useTranslation();

  const [ledgerSupportEmailAddress] = useSetting('server_configurations', 'support_email_address');
  const [proofOfAddressDocsRequirements] = useSetting(
    'app_features',
    'proof_of_address_document_requirements'
  );

  const { state } = useContext(UpgradeAccountContext);

  const { verificationNoteType, tiersStandard, riskTier, selectedSourceOfWealth } = state;

  const verificationNotes = useMemo(() => {
    switch (verificationNoteType) {
      case 'proof_of_address': {
        const result = [];
        const lines = proofOfAddressDocsRequirements?.split('/') ?? [];
        lines.forEach((line) => {
          const points = line.split(':;') ?? [];
          if (points.length > 0) {
            const title = points[0];
            result.push({
              title,
              points: points[1]?.split(';') ?? [],
            });
          } else {
            result.push({
              title: line,
              points: [],
            });
          }
        });
        return result;
      }
      case 'source_of_funds': {
        const selectedSourceOfWealthKey = riskTier?.source_of_wealth ?? selectedSourceOfWealth;
        const sourceOfWealth = tiersStandard.find((item) => item.key === selectedSourceOfWealthKey);
        if (sourceOfWealth) {
          const result = [];
          const lines = sourceOfWealth?.document_requirements
            ? sourceOfWealth?.document_requirements.split('/')
            : [];
          lines.forEach((line) => {
            const points = line.split(':;') ?? [];
            if (points.length > 0) {
              const title = points[0];
              result.push({
                title,
                points: points[1]?.split(';') ?? [],
              });
            } else {
              result.push({
                title: line,
                points: [],
              });
            }
          });
          return result;
        }
        return [];
      }
      default:
        return [];
    }
  }, [
    tiersStandard,
    verificationNoteType,
    proofOfAddressDocsRequirements,
    selectedSourceOfWealth,
    riskTier,
  ]);

  const documentRequirementHeader = (
    <div className="d-flex flex-row justify-content-start align-items-center">
      <i className="icon icon-info-icon text-info text-f4 me-2" />
      <p className="text-f6 text-dark">{translate('tier_update_level_document_requirment')}</p>
    </div>
  );

  return (
    <div className={`d-flex flex-column px-4 ${isMobile ? 'mt-3' : 'mt-5'} hide-scroll h-85vh`}>
      <p className="text-dark text-f3">{translate(`${verificationNoteType}_input_label`)}</p>
      {verificationNoteType !== 'pep' && (
        <div className="d-flex flex-column p-3 bg-info-light mt-4 rounded-extra">
          <div>{documentRequirementHeader}</div>
          {verificationNotes?.map((note) => (
            <div key={note?.title} className="mt-3">
              <p className="text-dark text-f6">{note?.title}</p>
              {note?.points?.length > 0 && (
                <ul className="text-info ps-4 py-0 m-0">
                  {note?.points.map((point) => (
                    <li key={point}>
                      <span className="text-f6 text-white">{point}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}
      {verificationNoteType === 'pep' && (
        <div className="d-flex flex-column gap-3">
          <div className="d-flex flex-column p-3 bg-info-light mt-4 rounded-extra">
            <div className="d-flex flex-row justify-content-start align-items-center">
              <Typography variant="small" as="p" className="text-dark">
                {translate('pep_definition_title')}
              </Typography>
            </div>
            <ul className="text-info ps-4 pt-2 m-0">
              {[1, 2, 3, 4, 5, 6, 7, 8]?.map((noteNumber) => (
                <li key={noteNumber}>
                  <Typography as="span" variant="small" className="text-white">
                    {translate(`pep_definition_note_${noteNumber}`)}
                  </Typography>
                </li>
              ))}
            </ul>
          </div>
          <Typography variant="small" as="p" className="text-dark">
            {translate('pep_definition_label_1')}
          </Typography>
          <Typography variant="small" as="p" className="text-dark">
            {translate('pep_definition_label_2')}
          </Typography>
          <Typography variant="small" as="p" className="text-dark">
            {translate('pep_definition_label_3')}
          </Typography>
        </div>
      )}
      <p className="text-shadow mt-4 letter-spacing-2" style={{ fontSize: '14px' }}>
        <Trans
          i18nKey="account_upload_file_contact_support_label"
          values={{
            support_email: ledgerSupportEmailAddress,
          }}
          components={[
            // eslint-disable-next-line jsx-a11y/anchor-has-content
            <a
              key="contact-link-2"
              className="text-decoration-none"
              href={`mailto:${ledgerSupportEmailAddress}`}
              rel="noreferrer"
            />,
          ]}
        />
      </p>
    </div>
  );
};

export default VerificationNotes;
